'use client'

/**
 * Client hook for fetching per-locale enablement settings.
 *
 * The backend's `/i18n/locale-settings` endpoint is the source of truth for
 * which locales are visible to end users. Editors see every locale regardless
 * (so they can review the translation in progress). The result is cached in
 * a module-level variable for the duration of the page session — admin
 * toggles propagate within the 60s revalidate window plus the cache TTL.
 */

import { useEffect, useState } from 'react'
import { getApiUrl } from '@/lib/serverConfig'
import { routing, type AppLocale } from '@/i18n/routing'
import type { LocaleSettings } from './overrides'

const TTL_MS = 60_000

// Mirrors the seed state of `i18n_locale_settings`. Launch policy: only English
// is publicly enabled; every other locale (ja, es, ar, ko) stays hidden until an
// admin enables it in /backstage. A transient `/i18n/locale-settings` failure
// must fall back to en-only rather than expose a not-yet-launched locale.
const defaultSettings: LocaleSettings = {
  en: { enabled: true, enabled_at: null },
  ja: { enabled: false, enabled_at: null },
  es: { enabled: false, enabled_at: null },
  ar: { enabled: false, enabled_at: null },
  ko: { enabled: false, enabled_at: null },
}

let cached: { value: LocaleSettings; fetchedAt: number } | null = null
let inflight: Promise<LocaleSettings> | null = null

async function fetchLocaleSettingsClient(): Promise<LocaleSettings> {
  if (cached && Date.now() - cached.fetchedAt < TTL_MS) return cached.value
  if (inflight) return inflight

  inflight = (async () => {
    try {
      const res = await fetch(`${getApiUrl()}/i18n/locale-settings`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = (await res.json()) as { settings?: LocaleSettings }
      const value = data.settings ?? defaultSettings
      cached = { value, fetchedAt: Date.now() }
      return value
    } catch {
      // Soft-fail to defaults; UI doesn't crash if the backend is slow / down.
      const value = cached?.value ?? defaultSettings
      cached = { value, fetchedAt: Date.now() }
      return value
    } finally {
      inflight = null
    }
  })()

  return inflight
}

/**
 * Invalidate the in-memory cache. Useful after the admin toggles a locale so
 * the switcher re-reads immediately.
 */
export function invalidateLocaleSettingsCache() {
  cached = null
}

/**
 * Client-side analog of `resolveAppLocale`'s `isLocalePubliclyEnabled`: true if
 * the locale is publicly visible per `i18n_locale_settings`. The default locale
 * is always enabled; a settings-fetch failure soft-falls to en-only (via
 * `fetchLocaleSettingsClient`), so a launch-gated locale can never slip through.
 * Used to gate *implicit* locale signals (e.g. `navigator.language`) before they
 * are persisted as a user's profile preference.
 */
export async function isLocalePubliclyEnabled(locale: AppLocale): Promise<boolean> {
  if (locale === routing.defaultLocale) return true
  const settings = await fetchLocaleSettingsClient()
  return settings[locale]?.enabled === true
}

/**
 * Returns per-locale settings + a derived list of locales the caller should
 * surface in general UI. Public users see enabled locales. Translators also
 * see the locales they have been explicitly assigned, even if still hidden.
 */
export function useLocaleSettings(opts?: { editorLocales?: AppLocale[] }): {
  settings: LocaleSettings
  visibleLocales: AppLocale[]
  isLoading: boolean
} {
  const [settings, setSettings] = useState<LocaleSettings>(cached?.value ?? defaultSettings)
  const [isLoading, setIsLoading] = useState<boolean>(cached == null)

  useEffect(() => {
    let cancelled = false
    fetchLocaleSettingsClient().then(next => {
      if (cancelled) return
      setSettings(next)
      setIsLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const editorLocaleSet = new Set(opts?.editorLocales ?? [])
  const visibleLocales: AppLocale[] = (routing.locales as readonly AppLocale[]).filter(
    loc => settings[loc]?.enabled === true || editorLocaleSet.has(loc),
  )

  return { settings, visibleLocales, isLoading }
}
